import { useMemo, useState } from 'react';
import { useWalletChartQuery } from '../../api/useWalletQueries';
import { fmtDate } from './walletPage.utils';
import type { ChartPoint } from './WalletPage.types';

/** Owns the wallet-usage chart's date range / reason filters and buckets the response into per-month points. */
export function useWalletChartData() {
  const defaultEnd = new Date();
  const defaultStart = new Date();
  defaultStart.setMonth(defaultStart.getMonth() - 5);
  defaultStart.setDate(1);

  const [chartStart, setChartStart] = useState(fmtDate(defaultStart));
  const [chartEnd, setChartEnd] = useState(fmtDate(defaultEnd));
  const [chartReason, setChartReason] = useState('');
  const chartQ = useWalletChartQuery(chartStart, chartEnd, chartReason || undefined);

  const chartData = useMemo<ChartPoint[]>(() => {
    const raw = chartQ.data?.data?.monthWiseData || [];
    const map = new Map<string, Record<string, unknown>>();
    raw.forEach((item: Record<string, unknown>) => {
      const mp = String(item.month ?? '').padStart(2, '0');
      const key = (item.monthKey as string) || `${item.year}-${mp}`;
      map.set(key, item);
    });
    const result: ChartPoint[] = [];
    const cur = new Date(chartStart);
    cur.setDate(1);
    const end = new Date(chartEnd);
    end.setDate(1);
    while (cur <= end) {
      const y = cur.getFullYear();
      const m = String(cur.getMonth() + 1).padStart(2, '0');
      const key = `${y}-${m}`;
      const label = `${cur.toLocaleString('default', { month: 'short' })} ${y}`;
      const d = map.get(key);
      const val = Number(
        (d?.totalDeposit as number) ||
          (d?.totalSpentAmount as number) ||
          (d?.amount as number) ||
          (d?.totalAmount as number) ||
          (d?.totalSpent as number) ||
          0,
      );
      result.push({ name: label, value: val });
      cur.setMonth(cur.getMonth() + 1);
    }
    return result;
  }, [chartQ.data, chartStart, chartEnd]);

  return {
    chartStart,
    setChartStart,
    chartEnd,
    setChartEnd,
    chartReason,
    setChartReason,
    chartData,
    isChartLoading: chartQ.isPending,
  };
}
